// Calendar icon vocabulary (DESIGN §4).
//
// Q16 forbids colour dots, and AGENTS.md §3 #19 forbids emoji as UI chrome.
// Every category therefore gets exactly one line icon at 15px, and the
// you/TA distinction is carried by **fill vs outline** rather than by colour —
// DESIGN §3.1's argument: 6 categories × 2 people is 12 combinations, and no
// 15px cell can resolve 12 hues, but a filled chip and an outlined chip read
// identically on every screen and for every colour-vision type.
//
// The three constraints DESIGN §4 places on this vocabulary:
//   1. no emoji
//   2. no colour dots
//   3. pairwise distinguishable at 15px
//
// **W48.0 re-cut the whole vocabulary, because the shipped one violated rule 3 in
// the most literal way available.** `trip` and `manual` were both `PenTool` — one
// icon appearing twice in the same seven-item legend. Two more were defensible
// but wrong for what they label: `Sun` for 休假 reads 「晴天」, and `Droplet` for
// 週期 reads as a bodily reference rather than as recurrence.
//
// The new set, and why each silhouette:
//   紀念日 Heart     heart        — kept
//   心願   Star      star         — kept
//   日記   BookOpen  open book    — kept
//   休假   TreePalm  palm         — holiday, not weather
//   行程   Plane     plane        — travel
//   週期   Flower2   flower       — W58.0, see below
//   手動   SquarePen box+pen      — "custom"
//
// **`manual` is `SquarePen`, not `Pencil`.** With `trip` now a `Plane`, a pencil
// would have *created* the collision this round exists to fix: both are
// elongated diagonal marks, and at 13px the eye cannot separate them. The square
// is a closed silhouette a plane can never be mistaken for.
//
// **`TreePalm`, not `Palmtree`: in lucide 0.460 `Palmtree` is only an alias
// export — the `createLucideIcon` name is `TreePalm`, and the canonical one is
// what the icon set documents.
//
// **W58.0 — 週期 由 `Repeat` 改成 `Flower2`。** 使用者指定「花花」。
// `Repeat` 讀作「循環規則」，而這個類別其實是一段有開始有結束的身體歷程。
//
// **W58.5 — `Flower2` 也被換掉了。** ADR-0204 的論證是「圖元少、13px 裝得下」，
// 但使用者在生產上看到的是「**內部圓點過多，縮小後像麻將一筒或蓮蓬頭**」。
// `Flower2` 的花頭是**四段圓弧 + 一個花心圓** —— ADR-0204 省下的是莖與葉
// （13px 之下本來就看不見），**沒省掉的是「內部點陣」**，而那正是問題本身。
//
// 現在是自繪的**單層五瓣剪影**（`CycleFlower.ts`，ADR-0209）。它仍然是 lucide
// icon 家族的一員（`createLucideIcon`），所以**這裡的型別不變** —— 而六個
// 既有消費者（格子的 chip、篩選面板、事件表單、日詳情、圖例、週期頁的歷史
// 清單）**自動換成新花，一行都不用改**。

import {
  BookOpen,
  Heart,
  Plane,
  SquarePen,
  Star,
  TreePalm,
  type LucideIcon,
} from "lucide-react";
import { CycleFlower } from "@/components/calendar/CycleFlower";
import type { CalendarCategory, ItemOwner } from "@/lib/api/calendar";

/** 繁體中文 labels (AGENTS.md §3 #14). */
export const CATEGORY_LABEL: Record<CalendarCategory, string> = {
  anniversary: "紀念日",
  wish: "心願",
  diary: "日記",
  leave: "休假",
  trip: "行程",
  cycle: "週期",
  manual: "手動",
};

export const CATEGORY_ICON: Record<CalendarCategory, LucideIcon> = {
  anniversary: Heart,
  wish: Star,
  diary: BookOpen,
  leave: TreePalm,
  trip: Plane,
  cycle: CycleFlower,
  manual: SquarePen,
};

/**
 * Categories offered in the filter panel, in the order they appear.
 * Projection categories first: they cost the user nothing to enable and are
 * the reason the calendar has content at all before they add anything.
 */
export const FILTER_CATEGORY_ORDER: CalendarCategory[] = [
  "anniversary",
  "wish",
  "diary",
  "leave",
  "trip",
  "cycle",
  "manual",
];

export const OWNER_LABEL: Record<ItemOwner, string> = {
  me: "你",
  partner: "TA",
};

export const VISIBILITY_LABEL = {
  shared: "共享",
  private: "僅我",
} as const;

/**
 * Chip styling per person: **fill for yours, outline for theirs.**
 *
 * The fill/outline split is the accessibility mechanism and stays structural,
 * so the two remain distinguishable in greyscale and for any colour-vision
 * type (DESIGN §3.1's argument: 6 categories × 2 people is 12 combinations and
 * no 15px cell resolves 12 hues).
 *
 * Colour then carries *identity* on top of it: yours is `--you`, hers is
 * `--partner` (the rose of DESIGN §2.1). She previously rendered as
 * `--ink-2`, which at 11px on cream paper read as **white** — an unlabelled
 * outlined glyph is indistinguishable from a disabled one, and "whose event
 * is this" is the first question the grid has to answer.
 *
 * `--*-strong` rather than `--*` on the icon: at 11px, plain `partner`
 * (220 100 130) measures ~2.9:1 against `--paper` and fails WCAG AA, while
 * `partner-strong` (165 60 95) measures ~5.4:1. This mirrors how the `you`
 * chip already uses `you-strong`.
 */
export function chipClasses(owner: ItemOwner): string {
  return owner === "me"
    ? "bg-you-tint border border-you text-you-strong"
    : "bg-transparent border border-partner/40 text-partner-strong";
}

/**
 * Accessible name for a chip: "休假，你，全日" (DESIGN §9).
 * The `count` suffix appears when several rows collapsed into one chip, so a
 * screen-reader user is told the day is busier than one chip suggests.
 */
export function chipAriaLabel(args: {
  category: CalendarCategory;
  owner: ItemOwner;
  allDay: boolean;
  count: number;
}): string {
  const base = `${CATEGORY_LABEL[args.category]}，${OWNER_LABEL[args.owner]}，${
    args.allDay ? "全日" : "有時刻"
  }`;
  return args.count > 1 ? `${base}，共 ${args.count} 項` : base;
}
